import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

// The API rejects requests carrying unknown fields. Some hook args include
// client-only keys (e.g. `refetchKey` to bust the RTK Query cache), so they are
// stripped here before the body is serialized.
const omitFields = (arg: Record<string, unknown>, keys: string[]) =>
  Object.fromEntries(Object.entries(arg ?? {}).filter(([key]) => !keys.includes(key)))
const CLIENT_ONLY_FIELDS = ["refetchKey"]

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_MAIN_URL,
  }),
  endpoints: (builder) => ({
    getEmployee: builder.query({
      query: () => ({
        method: "POST",
        url: "GetEmployee",
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),
    getShiftTransactions: builder.query({
      query: (arg) => ({
        method: "POST",
        url: "GetShiftTransactions",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body: omitFields(arg, CLIENT_ONLY_FIELDS),
      }),
    }),
    getCashierList: builder.query({
      query: (body) => ({
        method: "POST",
        url: "GetCashierList",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),
    getUserList: builder.query({
      query: (body) => ({
        method: "POST",
        url: "GetUserList",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),
    searchUsers: builder.query({
      query: (arg) => ({
        method: "POST",
        url: "SearchUsers",
        headers: {
          "Content-Type": "application/json",
        },
        body: omitFields(arg, CLIENT_ONLY_FIELDS),
      }),
    }),
    createUser: builder.mutation({
      query: (body) => ({
        url: "CreateUser",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),
    sendVerificationCode: builder.mutation({
      query: (body) => ({
        url: "SendVerificationCode",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body,
      }),
    }),

    completeVerificationCode: builder.mutation({
      query: (body) => ({
        url: "CompleteVerificationCode",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body,
      }),
    }),

    checkVerificationCompleted: builder.query({
      query: (body) => ({
        url: "CheckVerificationCompleted",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),
    getUser: builder.query({
      query: (body) => ({
        url: "GetUser",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),

    setUser: builder.mutation({
      query: (body) => ({
        url: "GetUser",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),

    // kiosk changes: called getPlayerTransactions
    getPlayerTransaction: builder.query({
      query: (body) => ({
        url: "GetPlayerTransaction",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),

    // kiosk changes: called getPlayerTransactions
    setPlayerTransaction: builder.mutation({
      query: (arg) => ({
        url: "GetPlayerTransaction",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body: omitFields(arg, CLIENT_ONLY_FIELDS),
      }),
    }),

    updateUser: builder.mutation({
      query: (body) => ({
        url: "UpdateUser",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),
    add: builder.mutation({
      query: (body) => ({
        url: "Fill",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),
    remove: builder.mutation({
      query: (body) => ({
        url: "Bleed",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),
    cancelTransaction: builder.mutation({
      query: (body) => ({
        url: "CancelTransaction",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),

    getCancelTransactionDetails: builder.mutation({
      query: (body) => ({
        url: "GetCancelTransactionDetails",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),

    createShift: builder.mutation({
      query: (body) => ({
        url: "CreateShift",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),

    // CloseShift takes no request body (the shift is resolved from the token).
    closeShift: builder.mutation({
      query: () => ({
        url: "CloseShift",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
      }),
    }),

    getAdminSummary: builder.query({
      // GetAdminSummaryRequest has no paging — only transactionTypes/from/to.
      query: (arg) => ({
        url: "GetAdminSummary",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body: omitFields(arg, [...CLIENT_ONLY_FIELDS, "page", "perPage"]),
      }),
      keepUnusedDataFor: 0,
    }),
    getAdminTransaction: builder.query({
      query: (arg) => ({
        url: "GetAdminTransaction",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body: omitFields(arg, CLIENT_ONLY_FIELDS),
      }),
      keepUnusedDataFor: 0,
    }),

    getShiftHistory: builder.query({
      query: (body) => ({
        url: "GetShiftHistory",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),

    purchase: builder.mutation({
      query: (body) => ({
        url: "Purchase",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),

    redeem: builder.mutation({
      query: (body) => ({
        url: "RedeemBalance",
        method: "POST",
        headers: {
          "Content-Type": "application/json-patch+json",
        },
        body,
      }),
    }),
  }),

  keepUnusedDataFor: 0,
})

export const {
  useGetEmployeeQuery,
  useGetShiftTransactionsQuery,
  useGetCashierListQuery,
  useGetUserListQuery,
  useSearchUsersQuery,
  useCreateUserMutation,
  useGetUserQuery,
  useSetUserMutation,
  useGetPlayerTransactionQuery,
  useUpdateUserMutation,
  useAddMutation,
  useRemoveMutation,
  useCancelTransactionMutation,
  useGetCancelTransactionDetailsMutation,
  useCreateShiftMutation,
  useCloseShiftMutation,
  useGetShiftHistoryQuery,
  usePurchaseMutation,
  useRedeemMutation,
  useSetPlayerTransactionMutation,
  useGetAdminSummaryQuery,
  useGetAdminTransactionQuery,
  useSendVerificationCodeMutation,
  useCompleteVerificationCodeMutation,
  useCheckVerificationCompletedQuery,
} = userApi
